import * as React from 'react';

import { Check, ChevronsUpDown, X } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/TailwindComponents/base/Command/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/TailwindComponents/base/Tooltip/Tooltip';
import { cn } from '@/components/TailwindComponents/lib/utils';

interface SelectOptionType {
  value: string;
  label: string;
  key?: string;
  disabled?: boolean;
}
interface SelectGroupType {
  label: string;
  key?: string;
  options: SelectOptionType[];
}

export interface IMultiselectCombobox {
  options: (SelectOptionType | SelectGroupType)[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyResultsMessage?: string;
  classNames?: string;
  onSelect?: (values: string[]) => void;
  value?: string[];
  disabled?: boolean;
  showSelectedItems?: boolean;
  customSelectListFormatter?: (selectedItems: string[]) => string;
  /**
   * Width of the combobox
   */
  width?: 'auto' | 'full' | 'fit' | 'sm' | 'md' | 'lg' | 'xl' | string;
  /**
   * @description If true, enables search functionality in the dropdown. If false, hides the search input.
   */
  searchable?: boolean;
}

export function MultiselectCombobox({
  classNames,
  options = [],
  placeholder,
  searchPlaceholder,
  emptyResultsMessage,
  onSelect,
  value: initialValue = [],
  disabled,
  showSelectedItems = true,
  customSelectListFormatter,
  width = 'full',
  searchable = true,
}: IMultiselectCombobox) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<string[]>(initialValue || []);
  const [searchQuery, setSearchQuery] = React.useState('');
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Width configuration
  const getWidthClass = () => {
    if (
      typeof width === 'string' &&
      !['auto', 'full', 'fit', 'sm', 'md', 'lg', 'xl'].includes(width)
    ) {
      return width; // Custom width
    }

    const widthMap = {
      auto: 'w-auto',
      full: 'w-full',
      fit: 'w-fit',
      sm: 'w-32',
      md: 'w-48',
      lg: 'w-64',
      xl: 'w-80',
    };

    return widthMap[width as keyof typeof widthMap] || 'w-full';
  };

  // Update internal state when initialValue changes
  React.useEffect(() => {
    const newValue = Array.isArray(initialValue) ? initialValue : [];
    if (JSON.stringify(newValue) !== JSON.stringify(value)) {
      setValue(newValue);
    }
  }, [initialValue]);

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (disabled) {
        return;
      }
      setOpen(isOpen);
      if (!isOpen) {
        setSearchQuery('');
      }
    },
    [disabled],
  );

  const handleSelect = React.useCallback(
    (currentValue: string) => {
      // Ensure value is always an array
      const currentValueArray = Array.isArray(value) ? value : [];

      const newValue = currentValueArray.includes(currentValue)
        ? currentValueArray.filter((v) => v !== currentValue)
        : [...currentValueArray, currentValue];

      setValue(newValue);
      onSelect?.(newValue);
      setOpen(true);
    },
    [value, onSelect],
  );

  const findOptionLabel = React.useCallback(
    (valueToFind: string): string | undefined => {
      for (const item of options) {
        if ('options' in item) {
          const found = item.options.find((opt) => opt.value === valueToFind);
          if (found) return found.label;
        } else if (item.value === valueToFind) {
          return item.label;
        }
      }
      return undefined;
    },
    [options],
  );

  const selectedItemsList = value
    ? value?.map((v) => findOptionLabel(v)).filter(Boolean)
    : [];

  const getDisplayValue = () => {
    if (value?.length === 0) return placeholder || 'Select...';
    if (value?.length === 1) {
      return findOptionLabel(value[0]);
    }

    if (customSelectListFormatter && selectedItemsList?.length > 0) {
      return customSelectListFormatter((selectedItemsList as string[]) ?? []);
    }

    return `${value?.length || 0} items selected`;
  };

  const filterOptions = React.useCallback(
    (query: string): (SelectOptionType | SelectGroupType)[] => {
      if (!searchable || !query) return options;

      const normalizedQuery = query.toLowerCase();

      return options
        .map((group) => {
          if ('options' in group) {
            const filteredOptions = group.options.filter((option) =>
              option.label.toLowerCase().includes(normalizedQuery),
            );

            if (filteredOptions.length === 0) {
              return null;
            }

            return {
              ...group,
              options: filteredOptions,
            };
          } else {
            return group.label.toLowerCase().includes(normalizedQuery)
              ? group
              : null;
          }
        })
        .filter(Boolean) as (SelectOptionType | SelectGroupType)[];
    },
    [options, searchable],
  );

  const filteredOptions = React.useMemo(
    () => filterOptions(searchQuery),
    [filterOptions, searchQuery],
  );

  const hasXButton = value?.length > 0 && !disabled;
  const tooltipWidth = getWidthClass();

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.stopPropagation();
      e.preventDefault();

      setValue([]);
      onSelect?.([]);
      setOpen(false);
    },
    [disabled, onSelect],
  );

  const renderOptions = () => {
    return (filteredOptions || []).map((option) => {
      if ('options' in option) {
        return (
          <CommandGroup key={option.key || option.label} heading={option.label}>
            {option.options.map((opt) => (
              <CommandItem
                key={opt.key || opt.value}
                value={opt.value}
                disabled={disabled || opt.disabled}
                onSelect={handleSelect}
              >
                {opt.label}
                <Check
                  className={cn(
                    'ml-auto',
                    value?.includes(opt.value) ? 'opacity-100' : 'opacity-0',
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        );
      }
      return (
        <CommandItem
          key={option.key || option.value}
          value={option.value}
          disabled={disabled || option.disabled}
          onSelect={handleSelect}
        >
          {option.label}
          <Check
            className={cn(
              'ml-auto',
              value?.includes(option.value) ? 'opacity-100' : 'opacity-0',
            )}
          />
        </CommandItem>
      );
    });
  };

  const handleRemoveItem = React.useCallback(
    (e: React.MouseEvent, valueToRemove: string) => {
      if (disabled) return;

      e.stopPropagation();
      e.preventDefault();

      const newValue = value.filter((v) => v !== valueToRemove);
      setValue(newValue);
      onSelect?.(newValue);
    },
    [disabled, value, onSelect],
  );

  // Keyboard handler for accessibility
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent, callback: (e: any) => void) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        callback(e);
      }
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  ref={buttonRef}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={disabled}
                  className={cn(
                    'justify-between flex items-center h-auto min-h-[40px] max-h-[100px] py-1.5',
                    getWidthClass(),
                    classNames,
                    disabled && '!cursor-not-allowed !pointer-events-auto',
                  )}
                  style={disabled ? { cursor: 'not-allowed' } : undefined}
                >
                  {showSelectedItems && value?.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 flex-grow items-center overflow-y-auto max-h-[calc(100px-1rem)]">
                      {value?.length > 0 ? (
                        selectedItemsList.map((label, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center gap-1 rounded-md bg-[#EE3E5C] text-white px-2 py-1 text-xs font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>{label}</span>
                            {!disabled && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) =>
                                  handleRemoveItem(e, value[index])
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, (e2) =>
                                    handleRemoveItem(e2, value[index]),
                                  )
                                }
                                className="ml-0.5 rounded-full hover:bg-[#D63853] transition-colors cursor-pointer"
                                aria-label={`Remove ${label}`}
                              >
                                <X className="h-2 w-2" />
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <span className="text-muted-foreground font-normal text-sm">
                          {placeholder || 'Select...'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="flex-grow text-left truncate font-normal">
                      {getDisplayValue()}
                    </span>
                  )}
                  {hasXButton && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleClear}
                      onKeyDown={(e) => handleKeyDown(e, handleClear)}
                      className="ml-2 p-1 rounded-full hover:bg-muted/50 transition-colors shrink-0 cursor-pointer"
                      aria-label="Clear selection"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </span>
                  )}
                  <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            {value?.length > 0 && !showSelectedItems && (
              <TooltipContent className={tooltipWidth}>
                <div className="flex flex-col gap-1">
                  {selectedItemsList.map((label, index) => (
                    <span key={index}>{label}</span>
                  ))}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <PopoverContent
          align="start"
          className={cn('p-0', classNames)}
          style={{ width: buttonRef.current?.offsetWidth }}
        >
          <Command shouldFilter={false}>
            {searchable && (
              <CommandInput
                disabled={disabled}
                placeholder={searchPlaceholder || 'Search...'}
                className="h-9"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
            )}
            <CommandList>
              <CommandEmpty>
                {emptyResultsMessage || 'No results found.'}
              </CommandEmpty>
              {renderOptions()}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
